import { getAppLogoUrl } from '@/constants/appIcons';

interface AppLogoProps {
  shortName: string;
  displayName: string;
  /** `xs` (16px) sits inline with body text; `sm` (20px) and `md` (28px) stand alone. */
  size?: 'xs' | 'sm' | 'md';
}

const DIMENSIONS: Record<NonNullable<AppLogoProps['size']>, string> = {
  xs: 'w-4 h-4',
  sm: 'w-5 h-5',
  md: 'w-7 h-7',
};

const TEXT_SIZES: Record<NonNullable<AppLogoProps['size']>, string> = {
  xs: 'text-[10px]',
  sm: 'text-xs',
  md: 'text-sm',
};

/**
 * Renders either the brand logo for the app, or a coloured initial-letter fallback.
 * Callers sometimes derive the names from LLM-emitted content, so missing values
 * degrade to a "?" avatar instead of crashing the surrounding card.
 */
export function AppLogo({ shortName, displayName, size = 'md' }: AppLogoProps) {
  const logoUrl = getAppLogoUrl(shortName);
  const dimension = DIMENSIONS[size];
  const textSize = TEXT_SIZES[size];

  if (logoUrl) {
    return <img src={logoUrl} alt={displayName || shortName} className={`${dimension} object-contain`} />;
  }

  return (
    <div className={`${dimension} rounded bg-custom-sidebar-active flex items-center justify-center`}>
      <span className={`${textSize} font-semibold text-custom-text-primary`}>
        {(displayName || shortName || '?').charAt(0).toUpperCase()}
      </span>
    </div>
  );
}
